import React, { useState, useEffect, useImperativeHandle, useRef } from "react";
import "./card.css";
import "./shimmer.css";
import { safeRemoveNode } from "@charitystack/shared";

function Card(props) {
  const [iframesReady, setIframesReady] = useState(false);
  const [iframeErrors, setIframeErrors] = useState([]);
  const prevIframeStatesRef = useRef();
  const fieldBackgroundColor = props.backgroundColor || "#FFFFFF";

  // Add this new function to handle field focus
  const clearFieldError = (fieldType) => {
    setIframeErrors((prev) =>
      prev.filter((error) => {
        switch (fieldType) {
          case "number":
            return error !== "CARD_NUMBER_ERROR";
          case "security_code":
            return error !== "CVC_ERROR";
          case "expiration_date":
            return error !== "EXPIRY_ERROR";
          case "address.postal_code":
            return error !== "ZIP_ERROR";
          default:
            return true;
        }
      })
    );
  };

  useEffect(() => {
    if (props.iframeStates) {
      if (
        props.iframeStates.number.isFocused !==
        prevIframeStatesRef?.current?.number?.isFocused
      ) {
        clearFieldError("number");
      }
      if (
        props.iframeStates.expiration_date.isFocused !==
        prevIframeStatesRef?.current?.expiration_date?.isFocused
      ) {
        clearFieldError("expiration_date");
      }
      if (
        props.iframeStates.security_code.isFocused !==
        prevIframeStatesRef?.current?.security_code?.isFocused
      ) {
        clearFieldError("security_code");
      }
      if (
        props.iframeStates["address.postal_code"].isFocused !==
        prevIframeStatesRef?.current?.["address.postal_code"]?.isFocused
      ) {
        clearFieldError("address.postal_code");
      }
    }

    // Store current value for next render
    prevIframeStatesRef.current = props.iframeStates;
  }, [props.iframeStates]);

  useImperativeHandle(props.finixCardRef, () => ({
    checkIframeErrors: () => {
      if (!props.iframeStates) return false;
      const errors = [];
      if (props.iframeStates.number.errorMessages.length > 0) {
        errors.push("CARD_NUMBER_ERROR");
      }
      if (props.iframeStates.expiration_date.errorMessages.length > 0) {
        errors.push("EXPIRY_ERROR");
      }
      if (props.iframeStates.security_code.errorMessages.length > 0) {
        errors.push("CVC_ERROR");
      }
      if (
        props.iframeStates["address.postal_code"].errorMessages.length > 0 ||
        props.iframeStates["address.postal_code"].isDirty == false
      ) {
        errors.push("ZIP_ERROR");
      }
      console.error("iframeErrors", errors);

      setIframeErrors(errors);
      return errors;
    },
  }));

  useEffect(() => {
    // Inject custom styles
    const style = document.createElement("style");
    style.textContent = `
            .field-wrapper {
                height: 100% !important;
                width: 100% !important;
            }
            .field-wrapper iframe {
                height: 100% !important;
                width: 100% !important;
                background: transparent !important;
            }
            .fake-field {
                display: flex;
                align-items: center;
            }
        `;
    document.head.appendChild(style);

    // Clean up function to remove the injected style when component unmounts
    return () => {
      // Use safe removal utility to prevent NotFoundError in race conditions
      safeRemoveNode(style);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  const defineField = (elementId, type, placeholder) => {
    const { form } = props;
    const f = form.field(type, {
      placeholder,
      styles: {
        default: {
          backgroundColor: fieldBackgroundColor,
        },
        base: {
          fontSize: "14px",
          lineHeight: "20px",
          color: "#3A3429", // Tailwind's text-ink
          "::placeholder": {
            color: "#9A9285", // Tailwind's text-ink-muted
          },
          ":focus": {
            color: "#3A3429", // Ensure text color doesn't change on focus
          },
          ":-webkit-autofill": {
            "-webkit-text-fill-color": "#3A3429",
            "-webkit-box-shadow": `0 0 0px 1000px ${fieldBackgroundColor} inset`,
          },
        },
        invalid: {
          color: "#EF4444", // Tailwind's text-red-500
        },
      },
    });

    const element = document.getElementById(elementId);
    if (element) {
      // Use safe removal for all child elements
      while (element.firstChild) {
        safeRemoveNode(element.firstChild);
      }
      element.appendChild(f);
    }
  };

  useEffect(() => {
    setIframesReady(false);

    const initializeFields = () => {
      defineField("field-wrapper-number", "number", "");
      defineField("field-wrapper-expiration_date", "expiration_date", "");
      defineField("field-wrapper-security_code", "security_code", "");
      defineField(
        "field-wrapper-address_postal_code",
        "address.postal_code",
        ""
      );

      setTimeout(() => {
        setIframesReady(true);
      }, 2000);
    };

    if (props.form) {
      initializeFields();
    }
  }, [props.form, fieldBackgroundColor]);

  const fieldStyle = {
    borderRadius: "var(--rounded-md, 6px)",
    border: "var(--rounded-one, 1px) solid var(--Slate300, #E0D9CC)",
    background: fieldBackgroundColor,
    padding: "8px",
    marginBottom: "10px",
    height: "28px", // Fixed height for all fields
    boxSizing: "border-box",
    boxShadow: "none",
  };

  const cardFieldStyle = {
    ...fieldStyle,
    border: `var(--rounded-one, 1px) solid ${
      iframeErrors?.includes("CARD_NUMBER_ERROR")
        ? "var(--Red500, #EF4444)"
        : "var(--Slate300, #E0D9CC)"
    }`,
  };
  const cvvFieldStyle = {
    ...fieldStyle,
    border: `var(--rounded-one, 1px) solid ${
      iframeErrors?.includes("CVC_ERROR")
        ? "var(--Red500, #EF4444)"
        : "var(--Slate300, #E0D9CC)"
    }`,
  };
  const expiryFieldStyle = {
    ...fieldStyle,
    border: `var(--rounded-one, 1px) solid ${
      iframeErrors?.includes("EXPIRY_ERROR")
        ? "var(--Red500, #EF4444)"
        : "var(--Slate300, #E0D9CC)"
    }`,
  };
  const zipFieldStyle = {
    ...fieldStyle,
    border: `var(--rounded-one, 1px) solid ${
      iframeErrors?.includes("ZIP_ERROR")
        ? "var(--Red500, #EF4444)"
        : "var(--Slate300, #E0D9CC)"
    }`,
  };

  const labelStyle = {
    display: "block",
    marginBottom: "4px",
    fontSize: "14px",
    color: "var(--Slate700, #3A3429)",
  };

  const rowStyle = {
    display: "flex",
    gap: "10px",
  };

  // Helper function to get error message for each field
  const getErrorMessage = (errorType) => {
    switch (errorType) {
      case "CARD_NUMBER_ERROR":
        return "Please enter a valid card number";
      case "CVC_ERROR":
        return "Invalid CVV";
      case "EXPIRY_ERROR":
        return "Invalid expiration date";
      case "ZIP_ERROR":
        return "Invalid zip/postal code";
      default:
        return "";
    }
  };

  return (
    <div>
      <label
        className="text-sm font-medium text-ink-muted"
        htmlFor="field-wrapper-number"
      >
        Card Number
      </label>
      <div className="fake-field h-7" style={cardFieldStyle}>
        <div
          id="field-wrapper-number"
          style={{ width: "100%", height: "28px" }}
          className="field-wrapper"
        ></div>
      </div>
      {iframeErrors?.includes("CARD_NUMBER_ERROR") && (
        <p className="mt-1 text-xs text-red-500">
          {getErrorMessage("CARD_NUMBER_ERROR")}
        </p>
      )}
      <div style={rowStyle}>
        <div style={{ flex: 1 }}>
          <label
            className="text-sm font-medium text-ink-muted"
            htmlFor="field-wrapper-security_code"
          >
            CVV
          </label>
          <div className="fake-field h-7" style={cvvFieldStyle}>
            <div
              id="field-wrapper-security_code"
              style={{ width: "100%", height: "28px" }}
              className="field-wrapper"
            ></div>
          </div>
          {iframeErrors?.includes("CVC_ERROR") && (
            <p className="mt-1 text-xs text-red-500">
              {getErrorMessage("CVC_ERROR")}
            </p>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label
            className="text-sm font-medium text-ink-muted"
            htmlFor="field-wrapper-expiration_date"
          >
            Expiration
          </label>
          <div className="fake-field h-7" style={expiryFieldStyle}>
            <div
              id="field-wrapper-expiration_date"
              style={{ width: "100%", height: "28px" }}
              className="field-wrapper"
            ></div>
          </div>
          {iframeErrors?.includes("EXPIRY_ERROR") && (
            <p className="mt-1 text-xs text-red-500">
              {getErrorMessage("EXPIRY_ERROR")}
            </p>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <label
            className="text-sm font-medium text-ink-muted"
            htmlFor="field-wrapper-address_postal_code"
          >
            Zip/Postal
          </label>
          <div className="fake-field h-7" style={zipFieldStyle}>
            <div
              id="field-wrapper-address_postal_code"
              style={{ width: "100%", height: "28px" }}
              className="field-wrapper"
            ></div>
          </div>
          {iframeErrors?.includes("ZIP_ERROR") && (
            <p className="mt-1 text-xs text-red-500">
              {getErrorMessage("ZIP_ERROR")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Card;
